import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { API_URL } from '../../config/constants';
import toast from 'react-hot-toast';
import { GlassCard, GlassButton } from '../../components/ui/GlassComponents';

const Subscription = () => {
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [upgradeSuccess, setUpgradeSuccess] = useState(false);
  const [teamMemberCount, setTeamMemberCount] = useState(0);

  // Define subscription plans (matching backend configuration)
  const plans = [
    {
      id: 'small',
      name: 'Small',
      price: '£4.99',
      description: 'For small teams',
      userLimit: 5,
      features: [
        'Up to 5 team members',
        'Complete leave management system',
        'Email notifications',
        'Custom leave types',
        'Reporting & analytics',
        'Standard support'
      ]
    },
    {
      id: 'medium',
      name: 'Medium',
      price: '£9.99',
      description: 'For growing teams',
      userLimit: 12,
      features: [
        'Up to 12 team members',
        'Complete leave management system',
        'Email notifications',
        'Custom leave types',
        'Reporting & analytics',
        'Standard support'
      ]
    },
    {
      id: 'large',
      name: 'Large',
      price: '£14.99',
      description: 'For larger teams',
      userLimit: 20,
      popular: true,
      features: [
        'Up to 20 team members',
        'Complete leave management system',
        'Email notifications',
        'Custom leave types',
        'Reporting & analytics',
        'Standard support'
      ]
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      price: '£39.99',
      description: 'For large organizations',
      userLimit: 100,
      features: [
        'Up to 100 team members',
        'Complete leave management system',
        'Email notifications',
        'Custom leave types',
        'Reporting & analytics',
        'Priority support'
      ]
    }
  ];

  useEffect(() => {
    fetchSubscriptionStatus();
    fetchTeamMemberCount();
  }, []);

  const fetchTeamMemberCount = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(`${API_URL}/teams/members`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.members) {
          setTeamMemberCount(data.members.length);
        }
      }
    } catch (err) {
      console.error('Error fetching team member count:', err);
    }
  };

  const fetchSubscriptionStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }

      const response = await fetch(`${API_URL}/stripe/subscription-status`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setSubscriptionData(data.subscription);
        }
      }
    } catch (err) {
      console.error('Error fetching subscription details:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPlan = (planId) => {
    const selectedPlanData = plans.find(p => p.id === planId);
    
    // Check if team has too many users for selected plan
    if (teamMemberCount > selectedPlanData.userLimit) {
      setError(`You have ${teamMemberCount} team members, but the ${selectedPlanData.name} plan only supports up to ${selectedPlanData.userLimit} members. Please remove team members or choose a higher plan.`);
      return;
    }
    
    setSelectedPlan(planId);
    setError('');
  };

  const handleUpgrade = async (planId) => {
    // Validate plan selection first
    const selectedPlanData = plans.find(p => p.id === planId);
    if (teamMemberCount > selectedPlanData.userLimit) {
      setError(`You have ${teamMemberCount} team members, but the ${selectedPlanData.name} plan only supports up to ${selectedPlanData.userLimit} members. Please remove team members or choose a higher plan.`);
      return;
    }

    // Don't allow downgrade or same plan selection for active subscriptions
    if (subscriptionData && subscriptionData.plan && subscriptionData.status !== 'canceled') {
      const currentPlanIndex = plans.findIndex(p => p.id === subscriptionData.plan);
      const selectedPlanIndex = plans.findIndex(p => p.id === planId);
      
      if (selectedPlanIndex <= currentPlanIndex) {
        setError('You can only upgrade to a higher plan');
        return;
      }
    }

    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication required');
        setLoading(false);
        return;
      }

      // Use upgrade endpoint for active subscribers, create-checkout-session for cancelled or new ones
      const endpoint = subscriptionData && subscriptionData.plan && subscriptionData.status !== 'canceled'
        ? '/stripe/upgrade-subscription' 
        : '/stripe/create-checkout-session';

      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          plan: planId
        })
      });

      const data = await response.json();
      
      if (data.success) {
        if (data.url) {
          // Redirect to Stripe checkout for new subscriptions
          window.location.href = data.url;
        } else {
          // For upgrades, show success message and refresh subscription data
          setError('');
          toast.success('Subscription upgraded successfully! Redirecting to dashboard...');
          
          // Refresh subscription data and auth context to update team info
          await Promise.all([
            fetchSubscriptionStatus(),
            checkAuth() // This will refresh the team data with new plan limits
          ]);
          
          // Show success state
          setUpgradeSuccess(true);
          
          // Auto-redirect to dashboard after 3 seconds
          setTimeout(() => {
            navigate('/dashboard');
          }, 3000);
        }
      } else {
        setError(data.message || 'Failed to process upgrade');
      }
    } catch (err) {
      console.error('Error processing upgrade:', err);
      setError('Failed to process upgrade');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckout = async () => {
    if (!selectedPlan) {
      setError('Please select a plan to continue');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication required');
        setLoading(false);
        return;
      }

      const response = await fetch(`${API_URL}/stripe/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          plan: selectedPlan
        })
      });

      const data = await response.json();
      
      if (data.success && data.url) {
        window.location.href = data.url;
      } else {
        setError(data.message || 'Failed to create checkout session');
      }
    } catch (err) {
      console.error('Error creating checkout session:', err);
      setError('Failed to create checkout session');
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (loading && !subscriptionData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-blue-900/20 dark:to-purple-900/20">
        <div className="container mx-auto px-4 py-6">
          <GlassCard className="flex justify-center items-center h-64">
            <svg className="animate-spin h-10 w-10 text-primary-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </GlassCard>
        </div>
      </div>
    );
  }

  // Only show current plan if subscription is active (not cancelled)
  const currentPlan = subscriptionData && subscriptionData.status !== 'canceled' ? 
    plans.find(plan => plan.id === subscriptionData.plan) : null;
  
  // Get recommended plan based on team size
  const getRecommendedPlan = () => {
    if (teamMemberCount <= 5) return plans[0]; // Small
    if (teamMemberCount <= 12) return plans[1]; // Medium
    if (teamMemberCount <= 20) return plans[2]; // Large
    return plans[3]; // Enterprise
  };
  
  const recommendedPlan = getRecommendedPlan();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-blue-900/20 dark:to-purple-900/20">
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-4">Subscription Management</h1>
          <p className="text-lg text-gray-600 dark:text-gray-400">
            {currentPlan ? 'Manage your subscription or upgrade to unlock more features' : 
             `Choose the perfect plan for your team of ${teamMemberCount} member${teamMemberCount !== 1 ? 's' : ''}`}
          </p>
          
          {/* Team size info and recommendation */}
          {!currentPlan && teamMemberCount > 0 && (
            <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
              <p className="text-sm text-blue-700 dark:text-blue-300">
                💡 Based on your team size ({teamMemberCount} members), we recommend the <strong>{recommendedPlan.name} plan</strong> 
                which supports up to {recommendedPlan.userLimit} members.
              </p>
            </div>
          )}
        </div>

        {error && (
          <GlassCard className="mb-8 border-red-200 dark:border-red-800 bg-red-50/30 dark:bg-red-900/30">
            <div className="text-red-700 dark:text-red-400 px-4 py-3">
              {error}
            </div>
          </GlassCard>
        )}

        {/* Success Message */}
        {upgradeSuccess && (
          <GlassCard className="mb-8 border-green-200 dark:border-green-800 bg-green-50/30 dark:bg-green-900/30">
            <div className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg className="h-8 w-8 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold text-green-800 dark:text-green-200">
                      🎉 Subscription Upgraded Successfully!
                    </h3>
                    <p className="text-green-700 dark:text-green-300 mt-1">
                      Your plan has been updated and new features are now available. Redirecting to dashboard in a few seconds...
                    </p>
                  </div>
                </div>
                <GlassButton
                  onClick={() => navigate('/dashboard')}
                  className="ml-4 bg-green-600 hover:bg-green-700 text-white flex items-center"
                >
                  Continue to Dashboard
                  <svg className="ml-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </GlassButton>
              </div>
            </div>
          </GlassCard>
        )}

        {/* Current Subscription Section */}
        {currentPlan && (
          <div className="mb-12">
            <h2 className="text-2xl font-semibold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-6">Your Current Plan</h2>
            <GlassCard className="border-green-200 dark:border-green-700 bg-gradient-to-r from-green-50/30 to-emerald-50/30 dark:from-green-900/30 dark:to-emerald-900/30 relative overflow-hidden">
              {/* Glowing effect */}
              <div className="absolute top-4 right-4 w-4 h-4 bg-green-400 rounded-full animate-pulse shadow-lg shadow-green-400/50"></div>
            
            <div className="flex flex-col md:flex-row md:items-center md:justify-between">
              <div className="mb-6 md:mb-0">
                <div className="flex items-center mb-3">
                  <h3 className="text-3xl font-bold text-green-600 dark:text-green-400 mr-4">{currentPlan.name} Plan</h3>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                    ✓ Active
                  </span>
                </div>
                <p className="text-4xl font-bold text-gray-900 dark:text-white mb-2">
                  {currentPlan.price}
                  <span className="text-lg font-normal text-gray-500 dark:text-gray-400">/month</span>
                </p>
                <p className="text-gray-600 dark:text-gray-400 mb-4">{currentPlan.description}</p>
                
                {subscriptionData.nextBilling && (
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Next billing: {new Date(subscriptionData.nextBilling).toLocaleDateString()}
                  </p>
                )}
              </div>
              
              <div className="md:ml-8">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">What's included:</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {currentPlan.features.map((feature, index) => (
                    <div key={index} className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                      <svg className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      {feature}
                    </div>
                  ))}
                </div>
              </div>
            </div>
            </GlassCard>
          </div>
        )}

        {/* Available Plans Section */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-8 text-center">
            {currentPlan ? 'Upgrade Options' : 'Available Plans'}
          </h2>
          <p className="text-center text-gray-600 dark:text-gray-400 mb-8">
            {currentPlan ? 'Choose a higher plan to unlock more features and add more team members' : 'Select the perfect plan for your team size and needs'}
          </p>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-8">
          {plans.filter(plan => {
            if (!currentPlan) return true; // Show all plans if no current subscription
            
            const currentPlanIndex = plans.findIndex(p => p.id === currentPlan.id);
            const planIndex = plans.findIndex(p => p.id === plan.id);
            
            return planIndex > currentPlanIndex; // Only show higher plans for upgrades
          }).map((plan) => {
            const isCurrentPlan = currentPlan && currentPlan.id === plan.id;
            const isSelected = selectedPlan === plan.id;
            const isRecommended = !currentPlan && recommendedPlan && plan.id === recommendedPlan.id;
            const isTooSmall = teamMemberCount > plan.userLimit;
            
            return (
              <GlassCard
                key={plan.id}
                className={`relative transition-all duration-300 cursor-pointer transform hover:scale-105 ${
                  isSelected
                    ? 'border-primary-500 shadow-xl scale-105 ring-4 ring-primary-500/20'
                    : isTooSmall
                    ? 'border-red-300 dark:border-red-600 opacity-60 cursor-not-allowed'
                    : isRecommended
                    ? 'border-green-400 dark:border-green-500 shadow-lg'
                    : 'border-gray-200 dark:border-gray-700 hover:border-primary-300 hover:shadow-xl'
                } ${plan.popular ? 'border-purple-400 dark:border-purple-500' : ''}`}
                onClick={() => !isTooSmall && handleSelectPlan(plan.id)}
              >
                {/* Badges */}
                {plan.popular && !isRecommended && !isTooSmall && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10">
                    <span className="bg-gradient-to-r from-primary-500 to-primary-600 text-white px-4 py-2 rounded-full text-sm font-semibold shadow-lg">
                      🔥 Most Popular
                    </span>
                  </div>
                )}
                
                {/* Recommended Badge */}
                {isRecommended && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10">
                    <span className="bg-gradient-to-r from-green-500 to-green-600 text-white px-4 py-2 rounded-full text-sm font-semibold shadow-lg">
                      ✨ Recommended
                    </span>
                  </div>
                )}
                
                {/* Too Small Badge */}
                {isTooSmall && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10">
                    <span className="bg-gradient-to-r from-red-500 to-red-600 text-white px-4 py-2 rounded-full text-sm font-semibold shadow-lg">
                      ⚠️ Too Small
                    </span>
                  </div>
                )}
                
                <div className={`p-8 ${plan.popular ? 'pt-12' : ''}`}>
                  {/* Plan Header */}
                  <div className="text-center mb-8">
                    <h3 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-3">{plan.name}</h3>
                    <div className="mb-4">
                      <span className="text-5xl font-bold text-gray-900 dark:text-white">{plan.price}</span>
                      <span className="text-lg text-gray-500 dark:text-gray-400">/month</span>
                    </div>
                    <p className="text-gray-600 dark:text-gray-400">{plan.description}</p>
                  </div>
                  
                  {/* Features List */}
                  <div className="mb-8">
                    <ul className="space-y-4">
                      {plan.features.map((feature, index) => (
                        <li key={index} className="flex items-start text-gray-600 dark:text-gray-400">
                          <svg className="h-5 w-5 text-primary-500 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span className="text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  {/* Select Button */}
                  <GlassButton
                    onClick={() => handleUpgrade(plan.id)}
                    disabled={loading || isTooSmall}
                    className={`w-full font-semibold py-4 px-6 transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center ${
                      isTooSmall 
                        ? 'bg-gray-400 text-gray-700 cursor-not-allowed' 
                        : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white'
                    }`}
                  >
                    {loading ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    ) : null}
                    {isTooSmall 
                      ? `Need ${plan.userLimit}+ plan` 
                      : currentPlan 
                        ? `Upgrade to ${plan.name}` 
                        : `Choose ${plan.name}`
                    }
                  </GlassButton>
                  
                  {/* Warning for too small plans */}
                  {isTooSmall && (
                    <div className="mt-3 text-center">
                      <p className="text-xs text-red-600 dark:text-red-400">
                        Your team has {teamMemberCount} members, but this plan only supports {plan.userLimit}
                      </p>
                    </div>
                  )}
                  
                  {/* Trust indicators - only show for upgrades */}
                  {currentPlan && (
                    <div className="mt-4 text-center space-y-1">
                      <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center justify-center">
                        <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                        </svg>
                        Secure Payment
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Prorated billing</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Cancel anytime</p>
                    </div>
                  )}
                </div>
              </GlassCard>
            );
          })}
        </div>
      </div>
      </div>
    </div>
  );
};

export default Subscription;
